/**
 * @m8t-stack/brain-engine — F4 librarian: frontmatter parse/serialize.
 *
 * parseFile splits YAML frontmatter (delimited by leading `---\n…\n---\n`) from
 * the body, returning a FileEntry. Files with no frontmatter block get
 * `frontmatter: {}` and `body` = the whole input. serialize() round-trips.
 */
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";
import type { FileEntry } from "../types.js";

/**
 * Parse a raw markdown string into a FileEntry.
 * - `raw` is stored verbatim.
 * - Frontmatter is the YAML block between the leading `---\n` and closing `\n---\n`.
 * - `body` is everything after the closing `---\n` (byte-for-byte).
 * - Files without a frontmatter block: `frontmatter: {}`, `body` = full input.
 */
export function parseFile(path: string, raw: string, sha?: string): FileEntry {
  // Match `---\n<yaml_block>\n---\n` at the very start of the string.
  const match = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/s.exec(raw);
  if (!match) {
    return { path, frontmatter: {}, body: raw, raw, sha };
  }
  const yamlBlock = match[1];
  const body = match[2];
  let frontmatter: Record<string, unknown>;
  try {
    const parsed: unknown = parseYaml(yamlBlock);
    frontmatter =
      parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
  } catch {
    frontmatter = {};
  }
  return { path, frontmatter, body, raw, sha };
}

/**
 * Serialize frontmatter + body back to a markdown string.
 * Produces: `---\n<yaml>---\n<body>`
 * Body is preserved byte-for-byte.
 */
export function serialize(frontmatter: Record<string, unknown>, body: string): string {
  const yaml = stringifyYaml(frontmatter);
  return `---\n${yaml}---\n${body}`;
}
